import dayjs from "dayjs";
import { defineStore, storeToRefs } from "pinia";
import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
import { useLots } from "seedgreen-shared/queries/lot";
import { computed, ref } from "vue";
import { getWeekOfYearFromTypeString } from "seedgreen-shared/utils/week-of-year";
import { useMyCustomer } from "seedgreen-shared/queries/customer";
import _ from "lodash";
import {
	PlantingSortMethodKey,
	usePlantingSortMethod,
} from "@/composables/usePlantingSortMethod";
import {
	PlantingDisplayFieldKey,
	usePlantingDisplayField,
} from "@/composables/usePlantingDisplayField";
import { useRanches } from "seedgreen-shared/queries/ranch";
import {
	LotSortMethodKey,
	useLotSortMethod,
} from "@/composables/useLotSortMethod";
import {
	RanchSortMethodKey,
	useRanchSortMethod,
} from "@/composables/useRanchSortMethod";
import { LotDTO } from "seedgreen-shared/dtos/LotDTO";
import {
	LotDisplayFieldKey,
	useLotDisplayField,
} from "@/composables/useLotDisplayField";
import { useUserPosition } from "@/composables/useUserPosition";
import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";

const keysToPersist = [
	"searchBar",
	"selectedPlantingDisplayFieldKey",
	"selectedLotDisplayFieldKey",
	"selectedPlantingSortMethod",
	"selectedLotSortMethod",
	"selectedRanchSortMethod",
];

// Sets need special serialization
const setsToPersist = [
	"selectedRegions",
	"selectedCommodities",
	"selectedGrowers",
	"selectedRanches",
	"selectedHarvestWeeks",
];

export const useLotSearchStore = defineStore(
	"lotSearch",
	() => {
		const { myCustomer } = useMyCustomer();
		const {
			ranchesById,
			ranches,
			isLoading: isLoadingRanches,
		} = useRanches();

		const searchBar = ref("");
		const setSearchBar = (value: string) => (searchBar.value = value);

		// Filter selection
		const selectedPlantingDisplayFieldKey =
			ref<PlantingDisplayFieldKey>("wet-date");
		const selectedPlantingSortMethod =
			ref<PlantingSortMethodKey>("wet-asc");
		const selectedLotSortMethod = ref<LotSortMethodKey>("ranch");
		const selectedLotDisplayFieldKey = ref<LotDisplayFieldKey>("distance");
		const selectedRanchSortMethod = ref<RanchSortMethodKey>("name");
		const selectedRegions = ref<Set<number>>(new Set());
		const selectedCommodities = ref<Set<string>>(new Set());
		const selectedGrowers = ref<Set<number>>(new Set());
		const selectedRanches = ref<Set<number>>(new Set());
		const selectedHarvestWeeks = ref<Set<string>>(new Set());

		function $reset() {
			selectedPlantingDisplayFieldKey.value = "wet-date";
			selectedPlantingSortMethod.value = "wet-asc";
			selectedLotDisplayFieldKey.value = "distance";
			selectedLotSortMethod.value = "ranch";
			selectedRanchSortMethod.value = "name";
			selectedRegions.value.clear();
			selectedCommodities.value.clear();
			selectedGrowers.value.clear();
			selectedRanches.value.clear();
			selectedHarvestWeeks.value.clear();
		}

		// Sort/display field
		const plantingSortMethod = usePlantingSortMethod(
			selectedPlantingSortMethod,
		);
		const plantingDisplayField = usePlantingDisplayField(
			selectedPlantingDisplayFieldKey,
		);
		const lotSortMethod = useLotSortMethod(selectedLotSortMethod);
		const lotDisplayField = useLotDisplayField(selectedLotDisplayFieldKey);
		const ranchSortMethod = useRanchSortMethod(selectedRanchSortMethod);

		// Shared data queries
		const { plantings, isLoading: isLoadingPlantings } =
			useDefaultPlantingsQuery();

		const { lots, isLoading: isLoadingLots } = useLots();
		const plantingsByLotId = computed(() => {
			const result = new Map<number, PlantingDTO[]>();
			plantings.value?.forEach((p) => {
				p.lotIds.forEach((lotId) => {
					if (!result.has(lotId)) {
						result.set(lotId, []);
					}
					result.get(lotId)?.push(p);
				});
			});
			return result;
		});

		// Distinct sets of filter options
		const distinctCommodities = computed<string[]>(() =>
			Array.from(
				new Set(
					plantings.value
						?.filter((p) => p.commodityName)
						.map((p) => p.commodityName as string) ?? [],
				),
			),
		);
		const distinctGrowers = computed(() =>
			_.uniqBy(
				plantings.value
					?.filter((p) => p.growerId)
					.map((p) => ({
						name: p.growerName,
						id: p.growerId as number,
					})) ?? [],
				(g) => g.id,
			),
		);
		// Uses the first harvest date of a planting
		const distinctHarvestWeeks = computed(() => {
			if (!myCustomer.value) return [];

			const firstHarvestDates =
				plantings.value
					?.filter((p) => p.harvestDates.length > 0)
					.map((p) => p.harvestDates[0]) ?? [];
			const yearWeeks = firstHarvestDates.map((firstHarvestDate) =>
				getWeekOfYearFromTypeString(
					dayjs(firstHarvestDate).toDate(),
					myCustomer.value?.yearWeekType ?? "Iso",
					myCustomer.value?.startDayOfWeek,
				),
			);
			return _.uniqBy(yearWeeks, (yw) => yw.toString());
		});

		const filteredPlantings = computed<PlantingDTO[]>(() => {
			return (plantings.value ?? []).filter((planting) => {
				// Search bar filter
				if (searchBar.value?.length) {
					const normalized = searchBar.value.toLowerCase().trim();
					const searchfield = [
						planting.name ?? "",
						planting.ranchName ?? "",
						planting.growerName ?? "",
					]
						.join(" ")
						.toLowerCase();
					if (!searchfield.includes(normalized)) return false;
				}

				// Regions filter
				if (
					selectedRegions.value?.size &&
					!planting.regionIds.some((id) =>
						selectedRegions.value.has(id),
					)
				) {
					return false;
				}

				// Commodities filter
				if (
					selectedCommodities.value?.size &&
					!selectedCommodities.value.has(planting.commodityName ?? "")
				) {
					return false;
				}

				// Growers filter
				if (
					selectedGrowers.value?.size &&
					!selectedGrowers.value.has(planting.growerId ?? -1)
				) {
					return false;
				}

				// Harvest weeks filter
				if (selectedHarvestWeeks.value?.size) {
					const week = getWeekOfYearFromTypeString(
						dayjs(planting.harvestDates[0]).toDate(),
						myCustomer.value?.yearWeekType ?? "Iso",
						myCustomer.value?.startDayOfWeek,
					);
					if (!selectedHarvestWeeks.value.has(week.toString())) {
						return false;
					}
				}

				return true;
			});
		});

		const { position } = storeToRefs(useUserPosition());
		const sortedPlantings = computed<PlantingDTO[]>(() =>
			filteredPlantings.value.toSorted((a, b) =>
				plantingSortMethod.value.fn(a, b, position.value),
			),
		);

		const filteredLots = computed(() => {
			return (lots.value ?? []).filter((lot) => {
				// Search bar filter
				if (searchBar.value?.length) {
					const normalized = searchBar.value.toLowerCase().trim();
					const searchfield = [lot.fullName].join(" ").toLowerCase();
					if (!searchfield.includes(normalized)) return false;
				}

				// Ranch filter
				if (
					selectedRanches.value?.size &&
					!selectedRanches.value.has(lot.ranchId ?? -1)
				) {
					return false;
				}

				const ranch = ranchesById.value?.[lot.ranchId ?? -1];
				// Grower filter
				if (
					selectedGrowers.value?.size &&
					(!ranch || !selectedGrowers.value.has(ranch.growerId ?? -1))
				) {
					return false;
				}

				return true;
			});
		});

		const sortedLots = computed<LotDTO[]>(() =>
			filteredLots.value.toSorted((a, b) =>
				lotSortMethod.value.fn(a, b, position.value),
			),
		);

		// Ranch filtering and sorting
		const filteredRanches = computed(() => {
			return (ranches.value ?? []).filter((ranch) => {
				// Search bar filter
				if (searchBar.value?.length) {
					const normalized = searchBar.value.toLowerCase().trim();
					const searchfield = [
						ranch.name ?? "",
						ranch.growerName ?? "",
					]
						.join(" ")
						.toLowerCase();
					if (!searchfield.includes(normalized)) return false;
				}

				// Grower filter
				if (
					selectedGrowers.value?.size &&
					!selectedGrowers.value.has(ranch.growerId ?? -1)
				) {
					return false;
				}

				return true;
			});
		});

		const sortedRanches = computed(() => {
			return filteredRanches.value.toSorted((a, b) =>
				ranchSortMethod.value.fn(a, b, position.value),
			);
		});

		return {
			$reset,

			searchBar,
			setSearchBar,
			plantingDisplayField,
			lotDisplayField,
			selectedPlantingDisplayFieldKey,
			selectedPlantingSortMethod,
			selectedLotDisplayFieldKey,
			selectedLotSortMethod,
			selectedRanchSortMethod,
			selectedRegions,
			selectedCommodities,
			selectedGrowers,
			selectedRanches,
			selectedHarvestWeeks,

			distinctCommodities,
			distinctGrowers,
			distinctHarvestWeeks,

			sortedPlantings,
			filteredPlantings,
			isLoadingPlantings,

			filteredLots,
			sortedLots,
			isLoadingLots,
			plantingsByLotId,

			filteredRanches,
			sortedRanches,
			isLoadingRanches,
		};
	},
	{
		persist: {
			key: "lot-search-filters",
			serializer: {
				serialize: (state) => {
					const result: Record<string, object> = {};
					keysToPersist.forEach((key) => {
						result[key] = state[key];
					});
					setsToPersist.forEach((key) => {
						result[key] = Array.from(state[key]);
					});
					return JSON.stringify(result);
				},
				deserialize: (str) => {
					const state = JSON.parse(str);
					setsToPersist.forEach((key) => {
						state[key] = new Set(state[key]);
					});
					return state;
				},
			},
		},
	},
);
